import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, HeartOff, Clock, Users, ChefHat, Flame, Beef, Wheat, Droplets, Share2, Copy, Check, Link2 } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { favoritesAPI } from "@/lib/api";

export default function RecipeDetail() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const recipe = location.state?.recipe;
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [favLoading, setFavLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!recipe) {
      navigate("/");
      return;
    }
    if (user) {
      favoritesAPI.list().then((r) => {
        const fav = r.data.find((f) => f.recipe?.id === recipe.id);
        if (fav) {
          setIsFavorite(true);
          setFavoriteId(fav.id);
        }
      }).catch(() => {});
    }

    // Screenshot protection: blur content when page loses focus
    const handleVisibility = () => {
      const el = document.querySelector('[data-testid="recipe-detail-page"]');
      if (!el) return;
      if (document.hidden) {
        el.style.filter = "blur(20px)";
        el.style.transition = "filter 0.15s";
      } else {
        setTimeout(() => { el.style.filter = "none"; }, 300);
      }
    };

    // Prevent right-click on recipe page
    const handleContext = (e) => e.preventDefault();

    // Prevent keyboard shortcuts for screenshots
    const handleKeydown = (e) => {
      if (e.key === "PrintScreen" || (e.metaKey && e.shiftKey && (e.key === "3" || e.key === "4" || e.key === "5"))) {
        e.preventDefault();
        const el = document.querySelector('[data-testid="recipe-detail-page"]');
        if (el) {
          el.style.filter = "blur(20px)";
          setTimeout(() => { el.style.filter = "none"; }, 1000);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    document.addEventListener("contextmenu", handleContext);
    document.addEventListener("keydown", handleKeydown);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("contextmenu", handleContext);
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [recipe, user, navigate]);

  // Schema.org JSON-LD for SEO rich snippets
  useEffect(() => {
    if (!recipe) return;
    const parseTime = (timeStr) => {
      if (!timeStr) return "";
      const match = timeStr.match(/(\d+)/);
      return match ? `PT${match[1]}M` : "";
    };
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Recipe",
      name: recipe.title,
      description: recipe.description || "",
      author: { "@type": "Organization", name: "Qu'est-ce qu'on mange ?" },
      datePublished: recipe.created_at || new Date().toISOString(),
      prepTime: parseTime(recipe.prep_time),
      cookTime: parseTime(recipe.cook_time),
      totalTime: parseTime(recipe.total_time),
      recipeYield: `${recipe.servings || 4} portions`,
      recipeCategory: "Plat principal",
      recipeCuisine: "International",
      recipeIngredient: recipe.ingredients || [],
      recipeInstructions: (recipe.instructions || []).map((step) => ({
        "@type": "HowToStep",
        text: step,
      })),
      keywords: (recipe.dietary_tags || []).join(", "),
    };
    if (recipe.nutrition_per_serving) {
      schema.nutrition = {
        "@type": "NutritionInformation",
        calories: `${recipe.nutrition_per_serving.calories} kcal`,
        proteinContent: `${recipe.nutrition_per_serving.proteins} g`,
        carbohydrateContent: `${recipe.nutrition_per_serving.carbs} g`,
        fatContent: `${recipe.nutrition_per_serving.fats} g`,
        fiberContent: `${recipe.nutrition_per_serving.fiber || 0} g`,
        sodiumContent: `${recipe.nutrition_per_serving.sodium || 0} mg`,
      };
    }
    if (recipe.health_score) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: recipe.health_score,
        ratingCount: 1,
        bestRating: 10,
        worstRating: 1,
      };
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "recipe-schema";
    const old = document.getElementById("recipe-schema");
    if (old) old.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("recipe-schema");
      if (el) el.remove();
    };
  }, [recipe]);

  const toggleFavorite = async () => {
    if (favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorite && favoriteId) {
        await favoritesAPI.remove(favoriteId);
        setIsFavorite(false);
        setFavoriteId(null);
      } else {
        const res = await favoritesAPI.add(recipe);
        setIsFavorite(true);
        setFavoriteId(res.data?.id);
      }
    } catch {
      // silent
    } finally {
      setFavLoading(false);
    }
  };

  if (!recipe) return null;

  const imgSrc = recipe.image_base64
    ? `data:image/jpeg;base64,${recipe.image_base64}`
    : null;

  // Build compact teaser data for shared link (no full image to keep URL small)
  const buildShareUrl = () => {
    const teaserData = {
      title: recipe.title,
      desc: recipe.description?.slice(0, 200) || "",
      prep: recipe.prep_time || "",
      cook: recipe.cook_time || "",
      serv: recipe.servings,
      hs: recipe.health_score,
      nut: recipe.nutrition_per_serving ? {
        cal: recipe.nutrition_per_serving.calories,
        prot: recipe.nutrition_per_serving.proteins,
        carb: recipe.nutrition_per_serving.carbs,
        fat: recipe.nutrition_per_serving.fats,
      } : null,
    };
    try {
      // UTF-8 safe base64 encoding
      const jsonStr = JSON.stringify(teaserData);
      const encoded = btoa(unescape(encodeURIComponent(jsonStr)));
      return `${window.location.origin}/shared?d=${encoded}`;
    } catch {
      return window.location.origin;
    }
  };

  const shareUrl = buildShareUrl();
  const teaserText = `${recipe.title}\n\n${recipe.description?.slice(0, 120) || ""}...\n\n${recipe.nutrition_per_serving ? `${recipe.nutrition_per_serving.calories} kcal | ${recipe.nutrition_per_serving.proteins}g prot.` : ""}`;

  const shareTeaser = () => {
    if (navigator.share) {
      navigator.share({
        title: recipe.title,
        text: teaserText,
        url: shareUrl,
      }).catch(() => {});
    } else {
      setShareOpen(true);
    }
  };

  const copyTeaser = () => {
    const text = `${teaserText}\n\nD\u00e9couvrez la recette compl\u00e8te : ${shareUrl}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Fallback: use textarea copy
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`*${recipe.title}*\n\n${recipe.description?.slice(0, 100) || ""}...\n\n${recipe.nutrition_per_serving ? `${recipe.nutrition_per_serving.calories} kcal par portion` : ""}\n\nVoir la recette : ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareTelegram = () => {
    const text = encodeURIComponent(`${recipe.title}\n\n${recipe.description?.slice(0, 100) || ""}...\n\n${recipe.nutrition_per_serving ? `${recipe.nutrition_per_serving.calories} kcal par portion` : ""}`);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${text}`, "_blank");
  };

  const shareSMS = () => {
    const text = encodeURIComponent(`${recipe.title} - ${recipe.nutrition_per_serving?.calories || ""} kcal\nVoir la recette : ${shareUrl}`);
    window.open(`sms:?body=${text}`, "_self");
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`Recette : ${recipe.title}`);
    const body = encodeURIComponent(`Salut !\n\nJ'ai trouv\u00e9 cette recette sur What To Cook :\n\n${recipe.title}\n${recipe.description?.slice(0, 150) || ""}\n\n${recipe.nutrition_per_serving ? `Calories: ${recipe.nutrition_per_serving.calories} kcal | Prot\u00e9ines: ${recipe.nutrition_per_serving.proteins}g` : ""}\n\nVoir la recette compl\u00e8te : ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
  };

  return (
    <div className="min-h-screen no-screenshot" data-testid="recipe-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={recipe.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-stone-900 flex items-center justify-center">
            <ChefHat className="w-20 h-20 text-stone-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/40 to-transparent" />
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="flex items-center gap-2 px-3 py-2 bg-black/50 backdrop-blur-sm text-white rounded-full text-sm hover:bg-black/70 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-20 relative z-10 pb-20">
        <div className="flex items-start justify-between gap-4 mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white">
            {recipe.title}
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Share Button */}
            <div className="relative">
              <button
                onClick={shareTeaser}
                data-testid="share-button"
                className="p-3 rounded-full bg-white/5 text-stone-400 hover:text-amber-500 hover:bg-amber-500/10 transition-all"
              >
                <Share2 className="w-5 h-5" />
              </button>
              {/* Share Dropdown (desktop fallback) */}
              {shareOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShareOpen(false)} />
                  <div className="absolute right-0 mt-2 w-64 bg-[#1C1917] border border-white/10 rounded-xl shadow-2xl py-2 z-50">
                    <p className="px-4 py-2 text-xs text-stone-500 uppercase tracking-wider font-semibold">{"Partager un aper\u00e7u"}</p>
                    <button
                      onClick={() => { copyTeaser(); setShareOpen(false); }}
                      data-testid="share-copy"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copié !" : "Copier le lien"}
                    </button>
                    <button
                      onClick={() => { shareWhatsApp(); setShareOpen(false); }}
                      data-testid="share-whatsapp"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => { shareTelegram(); setShareOpen(false); }}
                      data-testid="share-telegram"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                      Telegram
                    </button>
                    <button
                      onClick={() => { shareSMS(); setShareOpen(false); }}
                      data-testid="share-sms"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      SMS
                    </button>
                    <button
                      onClick={() => { shareEmail(); setShareOpen(false); }}
                      data-testid="share-email"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      Email
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Favorite Button */}
            {user && (
              <button
                onClick={toggleFavorite}
                disabled={favLoading}
                data-testid="favorite-button"
                className={`p-3 rounded-full transition-all ${
                  isFavorite
                    ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                    : "bg-white/5 text-stone-400 hover:text-red-400 hover:bg-red-500/10"
                }`}
              >
                {isFavorite ? <Heart className="w-5 h-5 fill-current" /> : <HeartOff className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {recipe.description && (
          <p className="text-stone-400 leading-relaxed mb-6">{recipe.description}</p>
        )}

        {/* Time/Servings */}
        <div className="flex flex-wrap gap-4 mb-8">
          {recipe.prep_time && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Clock className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("prepTime")}</p>
                <p className="text-sm text-white">{recipe.prep_time}</p>
              </div>
            </div>
          )}
          {recipe.cook_time && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Clock className="w-4 h-4 text-orange-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("cookTime")}</p>
                <p className="text-sm text-white">{recipe.cook_time}</p>
              </div>
            </div>
          )}
          {recipe.servings && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] border border-white/5 rounded-xl">
              <Users className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-[10px] uppercase text-stone-500 tracking-wider">{t("servings")}</p>
                <p className="text-sm text-white">{recipe.servings}</p>
              </div>
            </div>
          )}
        </div>

        {/* Dietary Tags */}
        {recipe.dietary_tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {recipe.dietary_tags.map((tag, i) => (
              <span key={i} className="bg-stone-800 text-stone-300 text-xs px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Nutrition Info */}
        {recipe.nutrition_per_serving && (
          <div className="mb-8" data-testid="nutrition-section">
            <h2 className="font-heading text-xl font-medium text-white mb-4">{t("nutritionInfo")}</h2>

            {/* Health Score */}
            {recipe.health_score != null && (
              <div className="flex items-center gap-3 mb-4 bg-[#1C1917] border border-white/5 rounded-2xl p-4">
                <div className="relative w-14 h-14 flex-shrink-0">
                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                    <circle cx="28" cy="28" r="24" fill="none" stroke="#292524" strokeWidth="4" />
                    <circle
                      cx="28" cy="28" r="24" fill="none"
                      stroke={recipe.health_score >= 7 ? "#22c55e" : recipe.health_score >= 4 ? "#f59e0b" : "#ef4444"}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${(recipe.health_score / 10) * 150.8} 150.8`}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">
                    {recipe.health_score}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{"Score sant\u00e9"}</p>
                  <p className="text-xs text-stone-500">sur 10</p>
                </div>
                {recipe.health_labels?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 ml-auto">
                    {recipe.health_labels.map((label, i) => (
                      <span key={i} className="bg-green-500/10 text-green-400 text-[10px] px-2.5 py-1 rounded-full border border-green-500/20">
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Macro Nutrients - Per Serving */}
            <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 mb-4">
                Par portion
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <NutritionCard
                  icon={<Flame className="w-4 h-4" />}
                  label={t("calories")}
                  value={recipe.nutrition_per_serving.calories}
                  unit="kcal"
                  color="text-orange-500"
                  bgColor="bg-orange-500/10"
                />
                <NutritionCard
                  icon={<Beef className="w-4 h-4" />}
                  label={t("proteins")}
                  value={recipe.nutrition_per_serving.proteins}
                  unit="g"
                  color="text-red-400"
                  bgColor="bg-red-500/10"
                />
                <NutritionCard
                  icon={<Wheat className="w-4 h-4" />}
                  label={t("carbs")}
                  value={recipe.nutrition_per_serving.carbs}
                  unit="g"
                  color="text-amber-500"
                  bgColor="bg-amber-500/10"
                />
                <NutritionCard
                  icon={<Droplets className="w-4 h-4" />}
                  label={t("fats")}
                  value={recipe.nutrition_per_serving.fats}
                  unit="g"
                  color="text-yellow-500"
                  bgColor="bg-yellow-500/10"
                />
              </div>

              {/* Additional nutrients */}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/5">
                {recipe.nutrition_per_serving.fiber != null && (
                  <div className="text-center">
                    <p className="text-xs text-stone-500">Fibres</p>
                    <p className="text-sm font-medium text-stone-300">{recipe.nutrition_per_serving.fiber}g</p>
                  </div>
                )}
                {recipe.nutrition_per_serving.sugar != null && (
                  <div className="text-center">
                    <p className="text-xs text-stone-500">Sucre</p>
                    <p className="text-sm font-medium text-stone-300">{recipe.nutrition_per_serving.sugar}g</p>
                  </div>
                )}
                {recipe.nutrition_per_serving.sodium != null && (
                  <div className="text-center">
                    <p className="text-xs text-stone-500">Sodium</p>
                    <p className="text-sm font-medium text-stone-300">{recipe.nutrition_per_serving.sodium}mg</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ingredients */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-medium text-white mb-4">{t("ingredients")}</h2>
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl p-5">
            <ul className="space-y-2.5">
              {recipe.ingredients?.map((ing, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-stone-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  {ing}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-medium text-white mb-4">{t("instructions")}</h2>
          <div className="space-y-4">
            {recipe.instructions?.map((step, i) => (
              <div key={i} className="flex gap-4 bg-[#1C1917] border border-white/5 rounded-2xl p-5">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-amber-500">{i + 1}</span>
                </div>
                <p className="text-sm text-stone-300 leading-relaxed pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NutritionCard({ icon, label, value, unit, color, bgColor }) {
  return (
    <div className="flex flex-col items-center gap-2 p-3 bg-stone-900/50 rounded-xl" data-testid={`nutrition-${label.toLowerCase()}`}>
      <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-white">{value}</p>
        <p className="text-[10px] text-stone-500 uppercase tracking-wider">{unit}</p>
      </div>
      <p className="text-xs text-stone-400">{label}</p>
    </div>
  );
}
